import React, { useRef } from 'react';
import ReactModal from 'react-modal';
import Draggable, { DraggableProps } from 'react-draggable';
import classNames from 'classnames';
import { ModalProps } from 'reactstrap';

export interface DraggableModalProps extends Omit<ModalProps, 'toggle'> {
    /** CSS selector of the drag handle inside the modal, e.g. "#draggable-123". */
    handle: string;
    toggle?: () => void;
}

const DraggableComponent = Draggable as React.ComponentType<Partial<DraggableProps>>;

// Hide the rest of the app from assistive tech while a modal is open (react-modal a11y setup).
ReactModal.setAppElement('#root');

const DraggableModal: React.FC<DraggableModalProps> = ({
    isOpen,
    toggle,
    handle,
    centered,
    size,
    backdrop = true,
    className,
    contentClassName,
    style,
    children,
}) => {
    const dialogRef = useRef<HTMLDivElement>(null);

    // Click on the empty .modal area (outside the dialog box) = backdrop click.
    const onHostClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && backdrop !== 'static') toggle?.();
    };

    return (
        <ReactModal
            isOpen={!!isOpen}
            onRequestClose={toggle}
            shouldCloseOnOverlayClick={backdrop !== 'static'}
            bodyOpenClassName="modal-open"
            overlayClassName="modal-backdrop show"
            className="modal d-block"
            style={{ overlay: { opacity: 1, backgroundColor: 'rgba(7, 9, 25, 0.8)' } }}
            contentElement={(props, modalChildren) => (
                <div {...props} onClick={onHostClick}>{modalChildren}</div>
            )}
        >
            <DraggableComponent handle={handle} nodeRef={dialogRef}>
                <div
                    ref={dialogRef}
                    role="document"
                    style={style}
                    className={classNames('modal-dialog', className, {
                        'modal-dialog-centered': centered,
                        [`modal-${size}`]: size,
                    })}
                >
                    <div className={classNames('modal-content', contentClassName)}>
                        {children}
                    </div>
                </div>
            </DraggableComponent>
        </ReactModal>
    );
};

export default DraggableModal;
